import { useQuery, useQueryClient } from "@tanstack/vue-query";
import type { ReadonlyRefOrGetter } from "@vueuse/core";
import {
  type BidDetailsProduct,
  type ProductDetails,
  SalesMethod,
} from "mws-ts-v2-sdk";

import { queries } from "~/queries";

type Options = {
  product: ReadonlyRefOrGetter<ProductDetails | BidDetailsProduct>;
  /**
   * If not provided, the next minimum bid for the given product will be used to calculate the bid costs.
   */
  bidAmount?: ReadonlyRefOrGetter<number>;
  enabled?: Ref<boolean>;
};

export const useBidCosts = (options: Options) => {
  const { userId } = useUser();
  const { shippingLocation } = useShippingLocation();

  const isQueryEnabled = computed(
    () =>
      (options.enabled?.value ?? true) &&
      toValue(options.product).salesMethod === SalesMethod.Bidding, // should not fetch bid costs for fixed price products
  );

  const { activeCurrency } = useActiveCurrency();

  const bid = computed(() => {
    const providedBid = toValue(options.bidAmount);

    if (providedBid) return providedBid;

    const nextMinimumBid = toValue(options.product)
      .availableQuickBidPrices?.[0];

    return nextMinimumBid
      ? getPriceInCurrency(nextMinimumBid, activeCurrency.value.isoCurrencyCode)
      : 0;
  });

  const bidCostsFilters = computed(() => ({
    userId: userId.value,
    productId: toValue(options.product).id,
    currency: activeCurrency.value.isoCurrencyCode,
    bid: bid.value,
    destinationCountryCode: shippingLocation.value,
  }));

  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const queryResponse = useQuery({
    ...queries.bidCosts.details(platformId, bidCostsFilters),
    placeholderData: (data) => data,
    enabled: isQueryEnabled,
  });

  const queryClient = useQueryClient();
  const ensureBidCostsQuery = () => {
    return queryClient.ensureQueryData(
      queries.bidCosts.details(platformId, bidCostsFilters),
    );
  };

  return {
    ...queryResponse,
    ensureBidCostsQuery,
    providedFilters: bidCostsFilters,
  };
};
